import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { clearReferral, pendingReferral } from '../../utils/referral';
import { codesService } from '../../services/affiliate.service';
import { trackUserSignup } from '../../components/analytics/GoogleAnalytics';
import { AuthLead, AuthShell, AuthTitle, DangerCallout, Field, FootNote, PasswordRules, PrimaryButton, TextLink, emailError, passwordRules } from './AuthShell';

export function SignUpView() {
    const navigate = useNavigate();
    const { signup } = useAuth();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [org, setOrg] = useState('');
    const [password, setPassword] = useState('');
    // Affiliate referral code. Prefilled from a ?ref= link; editable so a code heard
    // offline can be typed in.
    const [referral, setReferral] = useState(() => pendingReferral() ?? '');
    const [errors, setErrors] = useState<{ name?: string; email?: string; password?: string; referral?: string }>({});
    const [serverError, setServerError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const rules = useMemo(() => passwordRules(password), [password]);
    const clear = (k: keyof typeof errors) => setErrors((p) => (p[k] ? { ...p, [k]: undefined } : p));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const next: typeof errors = {};
        if (!name.trim()) next.name = 'Enter your name.';
        const em = emailError(email);
        if (em) next.email = em;
        if (!password) next.password = 'Choose a password.';
        else if (!rules.every((r) => r.ok)) next.password = 'Your password still misses the requirements below.';
        if (referral.trim() && !/^[A-Za-z0-9]{4,20}$/.test(referral.trim())) next.referral = 'Referral codes are 4-20 letters or digits.';
        setErrors(next);
        if (Object.keys(next).length) return;

        // This box replaces the ?ref= link code (applied below), so AuthContext must
        // not also send the link's code when the new user is set.
        const typedCode = referral.trim().toUpperCase();
        clearReferral();
        setIsLoading(true);
        setServerError(null);
        const result = await signup(name.trim(), email.trim(), password, org.trim() || undefined);
        if (result.success) {
            trackUserSignup('email');
            let codeNotice: { tone: 'success' | 'error' | 'warn'; text: string } | undefined;
            if (typedCode) {
                try {
                    const r = await codesService.apply(typedCode);
                    codeNotice = { tone: r.applied ? 'success' : 'warn', text: r.message };
                } catch (err: any) {
                    // The account exists either way; just tell them the code didn't apply.
                    codeNotice = { tone: 'error', text: `Akun dibuat, tapi kode referral ${typedCode} tidak bisa dipakai: ${err?.response?.data?.message || 'Kode tidak ditemukan.'}` };
                }
            }
            navigate('/', codeNotice ? { state: { codeNotice } } : undefined);
            return;
        }
        setServerError(result.error || 'Signup failed. Please try again.');
        setIsLoading(false);
    };

    return (
        <AuthShell>
            <AuthTitle lead="Create your" accent="account" />
            <AuthLead>One account for every site, invitation and catalog you build.</AuthLead>

            <form onSubmit={handleSubmit} noValidate>
                <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Field label="Full name" name="fullName" autoComplete="name" autoFocus value={name} onChange={(v) => { setName(v); clear('name'); }} placeholder="Bulan Feby" error={errors.name} />
                    <Field label="Email" type="email" name="email" autoComplete="email" value={email} onChange={(v) => { setEmail(v); clear('email'); }} placeholder="you@usaha.id" error={errors.email} />
                    <Field label="Business name" name="company" autoComplete="organization" optional value={org} onChange={setOrg} placeholder="Kopi Rakyat" />
                    <Field label="Referral code" name="referral" autoComplete="off" optional value={referral} onChange={(v) => { setReferral(v.toUpperCase()); clear('referral'); }} placeholder="BUDI10" error={errors.referral} />
                    <Field label="Password" type="password" name="password" autoComplete="new-password" value={password} onChange={(v) => { setPassword(v); clear('password'); }} placeholder="••••••••" error={errors.password} />
                </div>

                <PasswordRules rules={rules} hasError={!!errors.password} />

                {serverError && <DangerCallout>{serverError}</DangerCallout>}

                <PrimaryButton disabled={isLoading}>{isLoading ? 'Creating account…' : 'Create account'}</PrimaryButton>
            </form>

            <p style={{ margin: '16px 0 0', textAlign: 'center', fontSize: 12.5, lineHeight: 1.6, color: 'var(--hv-muted)' }}>
                By creating an account you agree to our{' '}
                <TextLink to="/terms" onClick={() => navigate('/terms')} muted>Terms</TextLink> and{' '}
                <TextLink to="/privacy" onClick={() => navigate('/privacy')} muted>Privacy Policy</TextLink>.
            </p>

            <FootNote marginTop={14}>
                Already have an account? <TextLink to="/login" onClick={() => navigate('/login')}>Log in</TextLink>
            </FootNote>
        </AuthShell>
    );
}
